/**
 * SEO utilities for generating proper canonical URLs and meta tags
 */

const ROOT_PATH = "/";
const FILE_PATH_PATTERN = /\/[^/?#]+\.[^/?#]+$/;

function splitPathSuffix(path: string): { pathname: string; suffix: string } {
  const match = path.match(/^([^?#]*)(.*)$/);
  return {
    pathname: match?.[1] || path,
    suffix: match?.[2] || "",
  };
}

export const stripTrailingSlash = (pathname: string): string => {
  if (pathname === ROOT_PATH) {
    return pathname;
  }

  return pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
};

export const normalizeSitePath = (path: string): string => {
  const withLeadingSlash = path.startsWith("/") ? path : `/${path}`;
  const { pathname, suffix } = splitPathSuffix(withLeadingSlash);

  if (pathname === ROOT_PATH || FILE_PATH_PATTERN.test(pathname) || pathname.endsWith("/")) {
    return `${pathname}${suffix}`;
  }

  return `${pathname}/${suffix}`;
};

/**
 * Get the canonical base URL for the site
 * Always returns the non-www version for consistency
 */
export const getCanonicalBaseUrl = (): string => {
  // SSR, hydration, and Pages previews must identify the same public URL.
  return 'https://elfletterbot.com';
};

/**
 * Generate a canonical URL for a given path
 * @param path The path (with or without leading slash)
 * @returns Full canonical URL
 */
export const getCanonicalUrl = (path: string): string => {
  const baseUrl = getCanonicalBaseUrl();
  const cleanPath = normalizeSitePath(path);
  return `${baseUrl}${cleanPath}`;
};

/**
 * Get the current page's canonical URL based on the current location
 * @returns Current page canonical URL
 */
export const getCurrentCanonicalUrl = (): string => {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    return getCanonicalUrl(path);
  }
  return getCanonicalBaseUrl();
};

/**
 * Generate Open Graph URL (should match canonical)
 * @param path The path for the page
 * @returns Open Graph URL
 */
export const getOgUrl = (path: string): string => {
  return getCanonicalUrl(path);
};

/**
 * Validate if a URL is using the canonical domain
 * @param url The URL to check
 * @returns true if using canonical domain, false if needs redirect
 */
export const isCanonicalDomain = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return !urlObj.hostname.startsWith('www.');
  } catch {
    return true; // If URL parsing fails, assume it's fine
  }
}; 
